import {
  LayoutSummaryFragment,
  OptimizationObjectiveType,
  OptimizationRunForDashboardFragment,
  OptimizationRunStatus,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  formatDateTime,
  formatInteger,
  formatTimespan,
} from '../../../common/formatHelper';
import useFormatter from '../../../common/useFormatter';
import { Stat } from '../../../components/stats/Stat';
import { StatGroup } from '../../../components/stats/StatGroup';
import { loadBalancingPolicyDocument } from '../../../loadBalancingPolicy/loadBalancingPolicy.state';

export type OptimisationStatsInfoProps = {
  opt: OptimizationRunForDashboardFragment;
  layoutSummary: LayoutSummaryFragment;
};

const OptimisationStatsInfo: React.FC<OptimisationStatsInfoProps> = props => {
  const { t } = useTranslation('simulation');
  const formatter = useFormatter();
  const balancingPolicy = useRecoilValue(loadBalancingPolicyDocument);

  const balancingPolicyRules = _.keyBy(balancingPolicy?.rules, r => r.id);

  const { opt } = props;
  const optStatus = opt?.status;
  const isStopping = optStatus === OptimizationRunStatus.STOPPING;

  const statusRaw = opt?.status;
  const titleStatus =
    statusRaw === OptimizationRunStatus.COMPLETED
      ? t(`Optimised`)
      : statusRaw === OptimizationRunStatus.CREATED
        ? t(`Preparing Data`)
        : statusRaw === OptimizationRunStatus.FAILED
          ? t(`Error`)
          : statusRaw === OptimizationRunStatus.IN_PROGRESS
            ? t(`Optimising`)
            : statusRaw === OptimizationRunStatus.STOPPING
              ? t(`Finalising Optimisation`)
              : '—';

  const lastStep = opt?.latestProgressDetails;

  const totalImprovementRaw = lastStep?.currentTotalImprovement ?? 0;
  const remainingTimeRaw = lastStep?.remainingComputeTime ?? 0;
  const elapsedTimeRaw = lastStep?.elapsedComputeTime ?? 0;
  const currentMoveCount = lastStep?.currentMoveCount ?? 0;
  const currentStepCount = lastStep?.step ?? 0;

  const totalImprovement = formatter.formatShare(totalImprovementRaw / 100);

  const objectivesTitles: Record<OptimizationObjectiveType, string> = {
    COST: t`Predicted Cost Improvement`,
    LOAD_BALANCING: t`Est. Balancing`,
  };

  function getTitle(
    type: OptimizationObjectiveType,
    ruleId: string,
    weight: number,
  ) {
    if (_.isNil(ruleId)) {
      return objectivesTitles[type];
    }
    return t('{{objType}} - {{ ruleTitle}}', {
      objType: objectivesTitles[type],
      ruleTitle: balancingPolicyRules[ruleId]?.title,
      ruleWeight: formatter.formatShare(weight).fullString,
    });
  }

  const costStats = _.filter(
    lastStep?.details?.improvement?.objectives,
    target => target.type === OptimizationObjectiveType.COST,
  );

  const loadBalancingStats = _.filter(
    lastStep?.details?.improvement?.objectives,
    target => target.type === OptimizationObjectiveType.LOAD_BALANCING,
  );

  const optimiseTime = formatTimespan(
    opt?.completedAt ?? 0,
    opt?.startedAt ?? 0,
  );

  const totalLocationCount = props.layoutSummary?.locationCount;

  // Efficiency and performance calculations
  const effectiveSteps = opt?.optimizationSummary?.effectiveSteps ?? 0;
  const moveCount = opt?.optimizationSummary?.moveCount ?? currentMoveCount;
  const elapsedTime = opt?.elapsedRunningTime ?? 0;

  const movesPerStep = effectiveSteps > 0 ? moveCount / effectiveSteps : 0;
  const improvementPerStep =
    effectiveSteps > 0 ? totalImprovementRaw / effectiveSteps : 0;
  const timePerStep = effectiveSteps > 0 ? elapsedTime / effectiveSteps : 0;
  const rateOfOptimisationTasksPerSec =
    elapsedTime > 0 ? effectiveSteps / elapsedTime : 0;
  const rateOfOptimisationMoves = elapsedTime > 0 ? moveCount / elapsedTime : 0;
  const rateOfSwaps =
    totalLocationCount > 0 ? (opt?.steps ?? 0) / totalLocationCount : 0;

  // Cost improvement calculations (fix: use decimal for formatter)
  const initialCost = opt?.initialCost ?? 0;
  const optimizedCost = opt?.optimizedCost ?? 0;
  const costImprovement =
    initialCost > 0 ? (initialCost - optimizedCost) / initialCost : 0;
  const reassignImprovement = opt?.reassignImprovement ?? 0;
  const optimizeImprovement = opt?.optimizeImprovement ?? 0;

  // Progress calculations
  const progress = opt?.progress ?? 0;
  const remainingTime =
    remainingTimeRaw > 0 ? formatTimespan(remainingTimeRaw * 1000) : '—';

  const optimisationStopReasonRaw = opt?.optimizationSummary?.stopReason;

  const stopReasonMapping = {
    STEP_LIMIT: t`Step Limit`,
    USER_REQUEST: t`User Request`,
    TARGET_IMPROVEMENT: t`Target Limit`,
    MIN_IMPROVEMENT_RATE: t`Improvement Rate Limit`,
    TIME_LIMIT: t`Time Limit`,
  };

  // Dual-source stat helpers
  function dualStat(
    finalVal: any,
    liveVal: any,
    formatter: (v: any) => string,
    labelFinal = t('Final'),
    labelLive = t('Live'),
  ) {
    if (
      finalVal !== undefined &&
      liveVal !== undefined &&
      finalVal !== liveVal
    ) {
      return `${formatter(finalVal)} (${labelFinal}) / ${formatter(liveVal)} (${labelLive})`;
    } else if (finalVal !== undefined) {
      return formatter(finalVal);
    } else if (liveVal !== undefined) {
      return formatter(liveVal);
    } else {
      return '—';
    }
  }

  // Final and live values for all major stats
  const moveCountFinal = opt?.optimizationSummary?.moveCount;
  const moveCountLive = opt?.latestProgressDetails?.currentMoveCount;
  const movesDisplay = dualStat(moveCountFinal, moveCountLive, formatInteger);

  const effectiveStepsFinal = opt?.optimizationSummary?.effectiveSteps;
  const effectiveStepsLive = opt?.latestProgressDetails?.step;
  const effectiveStepsDisplay = dualStat(
    effectiveStepsFinal,
    effectiveStepsLive,
    formatInteger,
  );

  const totalImprovementFinal =
    opt?.optimizationSummary?.improvement?.effectiveImprovement;
  const totalImprovementLive =
    opt?.latestProgressDetails?.currentTotalImprovement;
  const totalImprovementDisplay = dualStat(
    totalImprovementFinal,
    totalImprovementLive,
    v => formatter.formatShare((v ?? 0) / 100).fullString,
  );

  const initialCostFinal = opt?.initialCost;
  const optimizedCostFinal = opt?.optimizedCost;
  const costImprovementFinal =
    initialCostFinal && optimizedCostFinal
      ? (initialCostFinal - optimizedCostFinal) / initialCostFinal
      : undefined;
  // No live cost improvement, so just show final
  const costImprovementDisplay =
    costImprovementFinal !== undefined
      ? formatter.formatShare(costImprovementFinal).fullString
      : '—';

  const reassignImprovementFinal = opt?.reassignImprovement;
  const optimizeImprovementFinal = opt?.optimizeImprovement;
  const reassignImprovementDisplay =
    reassignImprovementFinal !== undefined
      ? formatter.formatShare(reassignImprovementFinal).fullString
      : '—';
  const optimizeImprovementDisplay =
    optimizeImprovementFinal !== undefined
      ? formatter.formatShare(optimizeImprovementFinal).fullString
      : '—';

  const currentStepCountFinal = opt?.latestProgressDetails?.step;
  const totalSteps = opt?.steps;
  const optimisationTasksDisplay =
    currentStepCountFinal !== undefined || totalSteps !== undefined
      ? `${formatInteger(currentStepCountFinal ?? 0)} / ${totalSteps ? formatInteger(totalSteps) : '—'}`
      : '—';

  // Efficiency metrics (use final if available, else live)
  const movesPerStepFinal =
    effectiveStepsFinal && moveCountFinal
      ? moveCountFinal / effectiveStepsFinal
      : undefined;
  const movesPerStepLive =
    effectiveStepsLive && moveCountLive
      ? moveCountLive / effectiveStepsLive
      : undefined;
  const movesPerStepDisplay = dualStat(movesPerStepFinal, movesPerStepLive, v =>
    v.toPrecision(3),
  );

  const improvementPerStepFinal =
    effectiveStepsFinal && totalImprovementFinal
      ? totalImprovementFinal / effectiveStepsFinal
      : undefined;
  const improvementPerStepLive =
    effectiveStepsLive && totalImprovementLive
      ? totalImprovementLive / effectiveStepsLive
      : undefined;
  const improvementPerStepDisplay = dualStat(
    improvementPerStepFinal,
    improvementPerStepLive,
    v => v.toPrecision(3) + '%',
  );

  const elapsedTimeFinal = opt?.elapsedRunningTime;
  const timePerStepFinal =
    effectiveStepsFinal && elapsedTimeFinal
      ? elapsedTimeFinal / effectiveStepsFinal
      : undefined;
  const timePerStepLive =
    effectiveStepsLive && elapsedTimeFinal
      ? elapsedTimeFinal / effectiveStepsLive
      : undefined;
  const timePerStepDisplay = dualStat(
    timePerStepFinal,
    timePerStepLive,
    v => v.toPrecision(3) + ' sec',
  );

  // Performance metrics
  const rateOfOptimisationTasksPerSecFinal =
    elapsedTimeFinal && effectiveStepsFinal
      ? effectiveStepsFinal / elapsedTimeFinal
      : undefined;
  const rateOfOptimisationTasksPerSecLive =
    elapsedTimeFinal && effectiveStepsLive
      ? effectiveStepsLive / elapsedTimeFinal
      : undefined;
  const optimisationRateDisplay = dualStat(
    rateOfOptimisationTasksPerSecFinal,
    rateOfOptimisationTasksPerSecLive,
    v => v.toPrecision(3) + ' task/sec',
  );

  const moveRateFinal =
    elapsedTimeFinal && moveCountFinal
      ? moveCountFinal / elapsedTimeFinal
      : undefined;
  const moveRateLive =
    elapsedTimeFinal && moveCountLive
      ? moveCountLive / elapsedTimeFinal
      : undefined;
  const moveRateDisplay = dualStat(moveRateFinal, moveRateLive, v =>
    v.toPrecision(3),
  );

  const swapRatio =
    totalLocationCount && opt?.steps
      ? (opt?.steps ?? 0) / totalLocationCount
      : undefined;
  const swapRatioDisplay =
    swapRatio !== undefined ? swapRatio.toPrecision(3) : '—';

  return (
    <>
      {/* STATUS GROUP */}
      <StatGroup
        title={t('Optimisation Status')}
        className="px-0.5 py-1"
        classNameTitle="!text-xs"
        classNameStatGroupItems="divide-y divide-menu-400/50 bg-app-panel-dark/60 !space-y-0.5"
      >
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Status:`}
          value={titleStatus}
        />
        {opt.optimizationSummary?.stopReason && (
          <Stat
            isMetaValue
            className="px-2 py-2"
            title={t`Stop Reason:`}
            value={stopReasonMapping[optimisationStopReasonRaw] ?? '—'}
          />
        )}
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Progress:`}
          value={`${progress.toFixed(1)}%`}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Started`}
          value={formatDateTime(new Date(opt?.startedAt))}
        />
        {opt?.completedAt && (
          <Stat
            className="px-2 py-2"
            isMetaValue
            title={t`Completed`}
            value={formatDateTime(new Date(opt?.completedAt))}
          />
        )}
        <Stat
          className="px-2 py-2"
          isMetaValue
          title={t`Total Time:`}
          titleAlt={`${formatDateTime(
            new Date(opt.completedAt),
          )} — ${formatDateTime(new Date(opt.startedAt))}`}
          value={optimiseTime}
        />
        <Stat
          className="px-2 py-2"
          isMetaValue
          title={t`Run Time`}
          value={formatTimespan(elapsedTime * 1000)}
        />
        {remainingTimeRaw > 0 && (
          <Stat
            className="px-2 py-2"
            isMetaValue
            title={t`Remaining Time`}
            value={remainingTime}
          />
        )}
      </StatGroup>

      {/* COST GROUP */}
      <StatGroup
        title={t('Cost & Improvement')}
        className="px-0.5 py-1"
        classNameTitle="!text-xs"
        classNameStatGroupItems="divide-y divide-menu-400/50 bg-app-panel-dark/60 !space-y-0.5"
      >
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Total Improvement:`}
          value={totalImprovementDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Cost Improvement:`}
          value={costImprovementDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Initial Cost:`}
          value={
            initialCostFinal !== undefined
              ? formatter.formatCurrency(initialCostFinal).fullString
              : '—'
          }
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Optimized Cost:`}
          value={
            optimizedCostFinal !== undefined
              ? formatter.formatCurrency(optimizedCostFinal).fullString
              : '—'
          }
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Reassign Improvement:`}
          value={reassignImprovementDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Optimize Improvement:`}
          value={optimizeImprovementDisplay}
        />
      </StatGroup>

      {/* EFFICIENCY GROUP */}
      <StatGroup
        title={t('Efficiency')}
        className="px-0.5 py-1"
        classNameTitle="!text-xs"
        classNameStatGroupItems="divide-y divide-menu-400/50 bg-app-panel-dark/60 !space-y-0.5"
      >
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Moves:`}
          value={movesDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Optimisation Tasks:`}
          value={optimisationTasksDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Effective Tasks:`}
          value={effectiveStepsDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Moves per Step:`}
          value={movesPerStepDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Improvement per Step:`}
          value={improvementPerStepDisplay}
        />
        <Stat
          isMetaValue
          className="px-2 py-2"
          title={t`Time per Step:`}
          value={timePerStepDisplay}
        />
      </StatGroup>

      {/* PERFORMANCE GROUP */}
      <StatGroup
        title={t('Performance')}
        className="px-0.5 py-1"
        classNameTitle="!text-xs"
        classNameStatGroupItems="divide-y divide-menu-400/50 bg-app-panel-dark/60 !space-y-0.5"
      >
        <Stat
          className="px-2 py-2"
          isMetaValue
          title={t`Optimisation Rate`}
          value={optimisationRateDisplay}
        />
        <Stat
          className="px-2 py-2"
          isMetaValue
          title={t`Move Rate (Moves/sec)`}
          value={moveRateDisplay}
        />
        <Stat
          className="px-2 py-2"
          isMetaValue
          title={t`Swap Ratio (Swaps/Location)`}
          value={swapRatioDisplay}
        />
        <Stat
          className="px-2 py-2"
          isMetaValue
          title={t`Locations (Total)`}
          value={formatInteger(totalLocationCount)}
        />
      </StatGroup>
    </>
  );
};

export default OptimisationStatsInfo;
